import { NumberField } from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { Trans, useI18n } from '~/common/context';
import { Button, Flex, FormFeedback, Icon, NumberInput } from '@wyz/ui';
import { invariant, useOnMount } from '@wyz/utils';
import { get } from 'lodash-es';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { DiameterField, usePricingPolicyForm } from '~/purchase/components';
import {
  CommonPricingPolicyHelpers,
  QuotePricingPolicyHelpers,
} from '~/purchase/helpers';
import {
  IQuotePolicyItem,
  IQuotePricingConfigurationStepForm,
} from '~/purchase/types';
// eslint-disable-next-line @wyz/module/no-relative-import
import './refinement-table.scss';

type RefinementRowProps = {
  policyItemIndex: number;
  refinementIndex: number;
  refinementFlags: IQuotePolicyItem['refinementFlags'];
  supplierTypeCodename: string;
  onRemoveRow: (idx: number) => void;
};

const RefinementRow = ({
  policyItemIndex,
  refinementIndex,
  refinementFlags,
  supplierTypeCodename,
  onRemoveRow,
}: RefinementRowProps) => {
  const [rowDefaultValue, setRowDefaultValue] = React.useState<number | ''>('');
  const pricingPolicyForm = usePricingPolicyForm();
  const baseName =
    `configurations.${policyItemIndex}.definition.supplierTypes.${supplierTypeCodename}.refinements.${refinementIndex}` as const;
  const methods = useFormContext<IQuotePricingConfigurationStepForm>();
  const selectedVehicleTypeGroup = useWatch({ name: 'vehicleTypeGroup' }) as
    | string
    | undefined;
  const { t } = useI18n();
  invariant(selectedVehicleTypeGroup);
  const vehicleTypeColumns = QuotePricingPolicyHelpers.getTyreTypes(
    t,
    pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
    selectedVehicleTypeGroup,
    refinementFlags,
  );
  const seasonsColumns = QuotePricingPolicyHelpers.getSeasons(
    pricingPolicyForm.seasons.optionItems,
    refinementFlags,
  );
  const selectedConfigurationType = useWatch({
    control: methods.control,
    name: `configurations.${policyItemIndex}.configurationType`,
  });
  const { state } = usePurchaseContext();

  return (
    <tr>
      <td>
        {refinementFlags.diameter ? (
          <DiameterField
            refinementIndex={refinementIndex}
            baseName={baseName}
            onRemoveRow={onRemoveRow}
            mode={pricingPolicyForm.mode}
          />
        ) : (
          <span>
            <Trans i18nKey='app.quote.pricing_policy.form.step1.all_diameters' />
          </span>
        )}
      </td>
      <td>
        {pricingPolicyForm.mode === 'WRITE' && (
          <NumberInput
            name='defaultValue'
            withKnobs={false}
            value={rowDefaultValue as number}
            size='sm'
            onChange={({ target }) => {
              const value = Number(target.value);
              setRowDefaultValue(value);
              seasonsColumns.forEach((season) => {
                vehicleTypeColumns.forEach((tyreType) => {
                  methods.setValue(
                    `${baseName}.seasons.${season.label}.tyreTypes.${tyreType.value}.value`,
                    value as never,
                    {
                      shouldValidate: true,
                    },
                  );
                });
              });
            }}
            placeholder='xx'
            sign={QuotePricingPolicyHelpers.getFieldSign(
              selectedConfigurationType,
              state.targetCustomer?.deliveryInformation?.country
                .currencyIsoCode ?? undefined,
            )}
          />
        )}
      </td>
      {seasonsColumns.map((season) => (
        <td key={season.value}>
          <Flex
            alignContent='center'
            justifyContent='center'
            className='space-x-2'
          >
            {vehicleTypeColumns.map((tyreType) => (
              <Flex
                key={`${tyreType.value}.${season.value}`}
                alignItems='center'
                style={{ width: '33.33%' }}
                className='space-x-2'
              >
                <NumberField
                  name={`${baseName}.seasons.${season.label}.tyreTypes.${tyreType.value}.value`}
                  withKnobs={false}
                  size='sm'
                  placeholder='xx'
                  onChangeValue={() => {
                    setRowDefaultValue('');
                  }}
                  feedbackType='tooltip'
                  sign={QuotePricingPolicyHelpers.getFieldSign(
                    selectedConfigurationType,
                    state.targetCustomer?.deliveryInformation?.country
                      .currencyIsoCode ?? undefined,
                  )}
                />
              </Flex>
            ))}
          </Flex>
        </td>
      ))}
    </tr>
  );
};

export const RefinementTableBody = ({
  refinementFlags,
  policyItemIndex,
  supplierTypeCodename,
}: {
  policyItemIndex: number;
  refinementFlags: IQuotePolicyItem['refinementFlags'];
  supplierTypeCodename: string;
}) => {
  const methods = useFormContext<IQuotePricingConfigurationStepForm>();
  const pricingPolicyForm = usePricingPolicyForm();
  const selectedConfigurationType = useWatch({
    control: methods.control,
    name: `configurations.${policyItemIndex}.configurationType`,
  });
  const configurationLabel =
    pricingPolicyForm.pricingConfigurationTypes.optionItems.find(
      (item) => item.value === selectedConfigurationType,
    )?.label;
  const selectedVehicleTypeGroup = useWatch<IQuotePricingConfigurationStepForm>(
    { name: 'vehicleTypeGroup' },
  ) as string | undefined;
  invariant(selectedVehicleTypeGroup);
  const {
    fields: rows,
    append,
    remove,
  } = useFieldArray<
    IQuotePricingConfigurationStepForm,
    `configurations.${number}.definition.supplierTypes.${string}.refinements`,
    'key'
  >({
    name: `configurations.${policyItemIndex}.definition.supplierTypes.${supplierTypeCodename}.refinements`,
  });
  const seasonsColumns = QuotePricingPolicyHelpers.getSeasons(
    pricingPolicyForm.seasons.optionItems,
    refinementFlags,
  );
  const { t } = useI18n();
  const vehicleTypeColumns = QuotePricingPolicyHelpers.getTyreTypes(
    t,
    pricingPolicyForm.vehicleTypeGroups.data?.data ?? [],
    selectedVehicleTypeGroup,
    refinementFlags,
  );

  useOnMount(() => {
    if (rows.length === 0) {
      append(
        QuotePricingPolicyHelpers.getInitialRefinement({
          flags: refinementFlags,
          seasons: pricingPolicyForm.seasons.optionItems,
          tyreTypes: vehicleTypeColumns,
        }),
      );
    }
  });

  const diameterError = get(
    methods.formState.errors,
    `configurations.${policyItemIndex}.definition.supplierTypes.${supplierTypeCodename}.refinements.root.message`,
  );

  return (
    <tbody>
      <tr>
        <td>
          <Flex justifyContent='between' alignItems='center'>
            <div>
              <span>
                <Trans i18nKey='app.quote.pricing_policy.form.step1.diameter_label' />
              </span>
            </div>

            <span>
              {refinementFlags.diameter &&
                pricingPolicyForm.mode === 'WRITE' && (
                  <Button
                    id='add-diameter'
                    variant='link'
                    size='sm'
                    onClick={() => {
                      const selected =
                        CommonPricingPolicyHelpers.extractSelectedDiameters(
                          methods.getValues(
                            `configurations.${policyItemIndex}.definition.supplierTypes.${supplierTypeCodename}.refinements`,
                          ),
                        );
                      const guess =
                        CommonPricingPolicyHelpers.guessNextDiameter(
                          selected.diameters,
                          selected.operators,
                        );

                      append(
                        QuotePricingPolicyHelpers.getInitialRefinement({
                          flags: refinementFlags,
                          seasons: pricingPolicyForm.seasons.optionItems,
                          tyreTypes: vehicleTypeColumns,
                          guessedDiameter: guess,
                        }),
                      );
                    }}
                  >
                    <Icon icon='plus' size='sm' />
                    <Trans i18nKey='app.pricing_policy.form.diameter_add_slice' />
                  </Button>
                )}
            </span>
          </Flex>
          {diameterError && (
            <FormFeedback
              invalid
              id={`configurations.${policyItemIndex}.definition.supplierTypes.${supplierTypeCodename}.refinements.error`}
            >
              <Trans i18nKey={diameterError} />
            </FormFeedback>
          )}
        </td>
        <td>{configurationLabel}</td>
        {seasonsColumns.map((season) => (
          <td key={season.value}>
            <Flex
              alignContent='center'
              justifyContent='center'
              className='space-x-2'
            >
              {vehicleTypeColumns.map((tyreType) => (
                <div
                  key={`${tyreType.value}.${season.value}`}
                  className='text-center'
                  style={{ width: '33.33%' }}
                >
                  {refinementFlags.tyreType ? tyreType.label : ''}
                </div>
              ))}
            </Flex>
          </td>
        ))}
      </tr>
      {rows.map((_, refinementIndex) => {
        return (
          <RefinementRow
            key={refinementIndex}
            policyItemIndex={policyItemIndex}
            refinementIndex={refinementIndex}
            refinementFlags={refinementFlags}
            supplierTypeCodename={supplierTypeCodename}
            onRemoveRow={remove}
          />
        );
      })}
    </tbody>
  );
};

export const RefinementTable = ({ children }: React.PropsWithChildren) => {
  return <table className='refinement-table'>{children}</table>;
};

export const RefinementTableHeader = ({
  flags,
}: {
  flags: IQuotePolicyItem['refinementFlags'];
}) => {
  const pricingPolicyForm = usePricingPolicyForm();
  const seasonsColumns = QuotePricingPolicyHelpers.getSeasons(
    pricingPolicyForm.seasons.optionItems,
    flags,
  );
  return (
    <thead>
      <tr>
        <th style={{ width: '30%' }}></th>
        <th style={{ width: '10%' }}></th>
        {seasonsColumns.map((season) => (
          <th
            key={season.value}
            style={{
              width: seasonsColumns.length === 1 ? '60%' : '23.3%',
            }}
          >
            {flags.season ? season.label : ''}
          </th>
        ))}
      </tr>
    </thead>
  );
};
